import { useState } from 'react';
import { Facebook, Instagram, Mail, MapPin, Send, Heart, Sparkles, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../Assets/TantikaLogo.png';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
    setEmail('');
    setTimeout(() => setSubscribed(false), 4000);
  };

  return (
    <footer className="bg-cocoa-950 text-cream-100">
      {/* Newsletter Strip */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-saffron-400" />
                Join Our Craft Community
              </h3>
              <p className="text-cream-100/70 max-w-md">
                Get updates on new arrivals, artisan stories, and exclusive offers.
              </p>
            </div>

            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full md:max-w-md">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cream-100/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/15 rounded-full text-cream-50 placeholder-cream-100/40 focus:outline-none focus:ring-2 focus:ring-saffron-400 focus:border-transparent transition"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-saffron-500 to-clay-500 text-white px-6 py-3.5 rounded-full font-semibold hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Subscribe
                <Send className="w-4 h-4" />
              </button>
            </form>

            {subscribed && (
              <p className="text-green-400 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Thanks for subscribing! Watch your inbox.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={logo} alt="তন্তিকা Logo" className="h-12 w-12 object-contain" />
              <span className="font-bengali text-3xl font-bold text-gradient-warm">তন্তিকা</span>
            </Link>
            <p className="text-cream-100/70 mb-5 leading-relaxed">
              Connecting authentic Bengali craftsmanship with people who value
              tradition, quality, and the hands behind every handmade piece.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://www.facebook.com/share/14YVS5LRfrh/"
                aria-label="Facebook"
                className="w-10 h-10 bg-white/5 border border-white/10 text-cream-100/70 rounded-full flex items-center justify-center hover:bg-saffron-500/20 hover:text-saffron-300 hover:border-saffron-400/40 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/shinchan121571?igsh=MTVieWYzd2FnajJibw=="
                aria-label="Instagram"
                className="w-10 h-10 bg-white/5 border border-white/10 text-cream-100/70 rounded-full flex items-center justify-center hover:bg-rose-500/20 hover:text-rose-300 hover:border-rose-400/40 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-5">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-cream-100/70 hover:text-saffron-300 transition-colors">Home</Link></li>
              <li><Link to="/shop" className="text-cream-100/70 hover:text-saffron-300 transition-colors">Products</Link></li>
              <li><Link to="/about" className="text-cream-100/70 hover:text-saffron-300 transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-cream-100/70 hover:text-saffron-300 transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h4 className="text-lg font-bold text-white mb-5">Customer Service</h4>
            <ul className="space-y-3">
              <li><Link to="/contact" className="text-cream-100/70 hover:text-saffron-300 transition-colors">FAQ</Link></li>
              <li><Link to="/shipping" className="text-cream-100/70 hover:text-saffron-300 transition-colors">Shipping Policy</Link></li>
              <li><Link to="/return-refund" className="text-cream-100/70 hover:text-saffron-300 transition-colors">Returns & Refunds</Link></li>
              <li><Link to="/privacy-policy" className="text-cream-100/70 hover:text-saffron-300 transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold text-white mb-5">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start text-cream-100/70">
                <Mail className="w-5 h-5 mr-3 text-saffron-400 shrink-0 mt-0.5" />
                <a href="mailto:tantikacustomercare@outlook.com" className="hover:text-saffron-300 transition-colors break-all">
                  tantikacustomercare@outlook.com
                </a>
              </div>
              <div className="flex items-center text-cream-100/70">
                <MapPin className="w-5 h-5 mr-3 text-saffron-400 shrink-0" />
                <span>West Bengal, India</span>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-saffron-300 font-semibold hover:text-saffron-200 group"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-cream-100/60 text-sm">
              &copy; {new Date().getFullYear()} তন্তিকা. All rights reserved.
            </p>
            <p className="text-cream-100/60 text-sm flex items-center gap-1.5">
              Handcrafted with <Heart className="w-4 h-4 text-rose-400 fill-rose-400" /> in Bengal
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
